import { Alert, Button, Card, Empty, Input, Modal, Select, Tag, Typography } from 'antd'
import { ArrowDownOutlined, ArrowUpOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import { useEffect, useState } from 'react'
import dayjs from 'dayjs'
import VSpace from '../../common/VSpace'
import { extractApiErrorMessage } from '../../../helpers/apiErrorMessage'
import { useSidebarTenantsQuery } from '../../MyTenant/tenantsQueries'
import { filterTenantOption, toTenantOptions } from '../../MyTenant/tenantSelectModel'
import { FeatureFlag, TRAFFIC_TYPE_TENANT } from '../../../domain/features/featureFlags'
import { useFeatureFlagSegmentsQuery, useSaveFeatureFlagMutation } from './featureFlagsQueries'
import {
  createFlagDraft,
  createRuleDraft,
  createTagDraft,
  FlagDraft,
  moveRule,
  removeTreatment,
  renameTreatment,
  RuleDraft,
  TAG_COLORS,
  TagDraft,
  toFlagSaveInput,
  validateFlagDraft,
} from './featureFlagsModel'

type Props = {
  open: boolean
  editing: FeatureFlag | null
  existingFlagIds: string[]
  onClose: () => void
}

const FeatureFlagEditorModal = ({ open, editing, existingFlagIds, onClose }: Props) => {
  const [draft, setDraft] = useState<FlagDraft>(() => createFlagDraft(editing))
  const [validationErrors, setValidationErrors] = useState<string[]>([])
  const { data: segments } = useFeatureFlagSegmentsQuery()
  const tenantsQuery = useSidebarTenantsQuery()
  const saveMutation = useSaveFeatureFlagMutation()

  const tenantOptionsFor = (selectedIds: number[]) =>
    toTenantOptions(tenantsQuery.data ?? [], { activeOnly: true, alwaysInclude: selectedIds })

  useEffect(() => {
    if (open) {
      setDraft(createFlagDraft(editing))
      setValidationErrors([])
      saveMutation.reset()
    }
  }, [open, editing])

  const handleSave = () => {
    const errors = validateFlagDraft(draft, { isNew: !editing, existingFlagIds })
    setValidationErrors(errors)
    if (errors.length > 0) {
      return
    }
    saveMutation.mutate(
      { id: draft.id.trim(), input: toFlagSaveInput(draft) },
      { onSuccess: onClose }
    )
  }

  const updateRule = (key: string, changes: Partial<RuleDraft>) => {
    setDraft((prev) => ({
      ...prev,
      rules: prev.rules.map((rule) => (rule.key === key ? { ...rule, ...changes } : rule)),
    }))
  }

  const updateTag = (key: string, changes: Partial<TagDraft>) => {
    setDraft((prev) => ({
      ...prev,
      tags: prev.tags.map((tag) => (tag.key === key ? { ...tag, ...changes } : tag)),
    }))
  }

  const tagColorOptions = TAG_COLORS.map((color) => ({
    value: color,
    label: <Tag color={color === 'default' ? undefined : color}>{color}</Tag>,
  }))

  const treatmentOptions = draft.treatments
    .filter((treatment) => treatment.trim().length > 0)
    .map((treatment) => ({ label: treatment, value: treatment }))

  const segmentOptions = (segments ?? []).map((segment) => ({
    label: segment.name,
    value: segment.id,
  }))

  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={handleSave}
      okText={editing ? 'Save' : 'Create'}
      confirmLoading={saveMutation.isLoading}
      title={editing ? `Edit Feature Flag '${editing.id}'` : 'New Feature Flag'}
      width={760}
      destroyOnHidden
    >
      <VSpace size="middle">
        {validationErrors.length > 0 && (
          <Alert
            type="error"
            message={
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {validationErrors.map((error) => (
                  <li key={error}>{error}</li>
                ))}
              </ul>
            }
          />
        )}
        {saveMutation.isError && (
          <Alert
            type="error"
            message={extractApiErrorMessage(saveMutation.error, 'Failed to save feature flag')}
          />
        )}

        <div>
          <Typography.Text strong>Flag Id</Typography.Text>
          {editing ? (
            <div>
              <Typography.Text code>{editing.id}</Typography.Text>
            </div>
          ) : (
            <Input
              placeholder="e.g. new-invoicing-flow"
              value={draft.id}
              onChange={(e) => setDraft((prev) => ({ ...prev, id: e.target.value }))}
            />
          )}
        </div>

        <div>
          <Typography.Text strong>Traffic Type</Typography.Text>
          <div>
            <Typography.Text type="secondary">
              {TRAFFIC_TYPE_TENANT} (flags are evaluated per tenant)
            </Typography.Text>
          </div>
        </div>

        <div>
          <Typography.Text strong>Treatments</Typography.Text>
          <VSpace size="small">
            {draft.treatments.map((treatment, index) => (
              <div key={index} style={{ display: 'flex', gap: 8 }}>
                <Input
                  value={treatment}
                  onChange={(e) => setDraft((prev) => renameTreatment(prev, index, e.target.value))}
                  style={{ width: 280 }}
                />
                <Button
                  type="text"
                  icon={<DeleteOutlined />}
                  onClick={() => setDraft((prev) => removeTreatment(prev, index))}
                />
              </div>
            ))}
            <Button
              icon={<PlusOutlined />}
              onClick={() => setDraft((prev) => ({ ...prev, treatments: [...prev.treatments, ''] }))}
            >
              Add Treatment
            </Button>
          </VSpace>
        </div>

        <div>
          <Typography.Text strong>Default Treatment</Typography.Text>
          <div>
            <Select
              value={draft.defaultTreatment}
              onChange={(defaultTreatment) => setDraft((prev) => ({ ...prev, defaultTreatment }))}
              options={treatmentOptions}
              style={{ width: 280 }}
            />
          </div>
          <Typography.Text type="secondary">
            Applied when no targeting rule matches.
          </Typography.Text>
        </div>

        <div>
          <Typography.Text strong>Targeting Rules</Typography.Text>
          <div>
            <Typography.Text type="secondary">
              Rules are evaluated in order — the first matching rule wins.
            </Typography.Text>
          </div>
          <VSpace size="small" style={{ marginTop: 8 }}>
            {draft.rules.length === 0 && (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No rules — every tenant gets the default treatment"
              />
            )}
            {draft.rules.map((rule, index) => (
              <Card key={rule.key} size="small" title={`Rule ${index + 1}`} extra={
                <>
                  <Button
                    type="text"
                    size="small"
                    icon={<ArrowUpOutlined />}
                    disabled={index === 0}
                    onClick={() => setDraft((prev) => ({ ...prev, rules: moveRule(prev.rules, index, 'up') }))}
                  />
                  <Button
                    type="text"
                    size="small"
                    icon={<ArrowDownOutlined />}
                    disabled={index === draft.rules.length - 1}
                    onClick={() => setDraft((prev) => ({ ...prev, rules: moveRule(prev.rules, index, 'down') }))}
                  />
                  <Button
                    type="text"
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => setDraft((prev) => ({
                      ...prev,
                      rules: prev.rules.filter((r) => r.key !== rule.key),
                    }))}
                  />
                </>
              }>
                <VSpace size="small">
                  <div>
                    <Typography.Text>Serve treatment</Typography.Text>{' '}
                    <Select
                      value={rule.treatment}
                      onChange={(treatment) => updateRule(rule.key, { treatment })}
                      options={treatmentOptions}
                      style={{ width: 200 }}
                      size="small"
                    />
                  </div>
                  <div>
                    <Typography.Text>To tenants in segments</Typography.Text>
                    <Select
                      mode="multiple"
                      value={rule.segments}
                      onChange={(ruleSegments) => updateRule(rule.key, { segments: ruleSegments })}
                      options={segmentOptions}
                      placeholder="Select segments"
                      style={{ width: '100%' }}
                      optionFilterProp="label"
                    />
                  </div>
                  <div>
                    <Typography.Text>Or these specific tenants</Typography.Text>
                    <Select
                      mode="multiple"
                      value={rule.ids}
                      onChange={(ids: number[]) => updateRule(rule.key, { ids })}
                      options={tenantOptionsFor(rule.ids)}
                      filterOption={filterTenantOption}
                      loading={tenantsQuery.isLoading}
                      placeholder="Search tenants by name or id"
                      style={{ width: '100%' }}
                    />
                  </div>
                </VSpace>
              </Card>
            ))}
            <Button
              icon={<PlusOutlined />}
              onClick={() => setDraft((prev) => ({
                ...prev,
                rules: [...prev.rules, createRuleDraft(prev.treatments, prev.defaultTreatment)],
              }))}
            >
              Add Rule
            </Button>
          </VSpace>
        </div>

        <div>
          <Typography.Text strong>Tags</Typography.Text>
          <VSpace size="small" style={{ marginTop: 8 }}>
            {draft.tags.map((tag) => (
              <div key={tag.key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Select
                  value={tag.color ?? 'default'}
                  onChange={(color) => updateTag(tag.key, { color })}
                  options={tagColorOptions}
                  style={{ width: 130 }}
                />
                <Input
                  placeholder="Tag label"
                  value={tag.label}
                  onChange={(e) => updateTag(tag.key, { label: e.target.value })}
                  style={{ width: 240 }}
                />
                {tag.label.trim().length > 0 && (
                  <Tag color={tag.color && tag.color !== 'default' ? tag.color : undefined}>
                    {tag.label}
                  </Tag>
                )}
                {tag.timestamp && (
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {dayjs(tag.timestamp).format('MMM D, YYYY h:mm A')}
                  </Typography.Text>
                )}
                <Button
                  type="text"
                  icon={<DeleteOutlined />}
                  onClick={() => setDraft((prev) => ({
                    ...prev,
                    tags: prev.tags.filter((t) => t.key !== tag.key),
                  }))}
                  style={{ marginLeft: 'auto' }}
                />
              </div>
            ))}
            <Button
              icon={<PlusOutlined />}
              onClick={() => setDraft((prev) => ({ ...prev, tags: [...prev.tags, createTagDraft()] }))}
            >
              Add Tag
            </Button>
          </VSpace>
        </div>
      </VSpace>
    </Modal>
  )
}

export default FeatureFlagEditorModal
